import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { taskService } from '../services/tasks';
import { authService } from '../services/auth';

function TaskView({ showToast }) {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('checklist'); // Default to checklist tab
  const [isEditing, setIsEditing] = useState(false);
  const [originalText, setOriginalText] = useState('');
  const [editData, setEditData] = useState({
    taskName: '',
    tags: [],
    contacts: [],
    places: [],
    checklist: [],
    notifications: []
  });
  const [newTag, setNewTag] = useState('');
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });
  const [newPlace, setNewPlace] = useState({ name: '', address: '' });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newNotification, setNewNotification] = useState({ 
    type: 'once', 
    dateTime: '', 
    rrule: '', 
    interval: '', 
    description: '' 
  });
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  
  // Use refs for stable references
  const mediaRecorderRef = useRef(null);
  const isRecordingRef = useRef(false);

  // Timer for recording duration
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else {
      setRecordingDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, recordingStartTime]);

  useEffect(() => {
    const loadTask = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading task:', taskId);
        
        // Check if we have a token from URL
        const token = searchParams.get('token');
        console.log('🔑 Token from URL:', token ? 'EXISTS' : 'NOT FOUND');
        
        // Check if we're in Telegram Web App
        const { telegramService } = await import('../services/telegram');
        console.log('📱 Is Telegram Web App:', telegramService.isTelegramWebApp);
        
        if (telegramService.isTelegramWebApp) {
          console.log('🔄 Authenticating with Telegram Web App...');
          // Use Telegram Web App authentication
          const telegramData = telegramService.getUser();
          if (telegramData) {
            await authService.authenticateWithTelegram(telegramData);
            console.log('✅ Telegram authentication successful');
          }
        } else if (token) {
          console.log('🔄 Exchanging one-time token...');
          // Exchange one-time token for JWT (fallback for non-Telegram usage)
          await authService.exchangeToken(token);
          console.log('✅ Token exchanged successfully');
        }

        console.log('🔄 Loading task details...');
        // Load task details
        const taskData = await taskService.getTaskById(taskId);
        console.log('📋 Task data received:', taskData);
        console.log('👥 Contacts:', taskData.contacts);
        console.log('📍 Places:', taskData.places);
        setTask(taskData);
        
        // Initialize edit data
        setEditData({
          taskName: taskData.text || '',
          tags: taskData.tags || [],
          contacts: taskData.contacts || [],
          places: taskData.places || [],
          checklist: taskData.checklist || [],
          notifications: taskData.notifications || []
        });
        setHasUnsavedChanges(false);
        
        // Auto-enter edit mode for pending tasks, view mode for others
        console.log('🔍 Task status:', taskData.status);
        console.log('🔍 Task data:', taskData);
        
        // Check for edit parameter in URL
        const editParam = searchParams.get('edit');
        console.log('🔍 Edit parameter:', editParam);
        
        if (taskData.status === 'pending' || editParam === 'true') {
          console.log('📝 Setting edit mode for pending task or edit=true');
          setIsEditing(true);
        } else {
          console.log('👁️ Setting view mode for non-pending task');
          setIsEditing(false);
        }
      } catch (err) {
        console.error('❌ Error loading task:', err);
        console.error('❌ Error details:', {
          message: err.message,
          stack: err.stack,
          taskId,
          token: searchParams.get('token')
        });
        setError(`Ошибка загрузки задачи: ${err.message}`);
        showToast(`❌ Ошибка загрузки задачи: ${err.message}`, 'error');
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      loadTask();
    }
  }, [taskId, searchParams]);

  // Keyboard detection effect
  useEffect(() => {
    const handleResize = () => {
      // Detect if keyboard is open by comparing window height with viewport height
      const keyboardThreshold = 150; // Threshold in pixels
      const currentIsKeyboardOpen = window.innerHeight - window.visualViewport?.height > keyboardThreshold;
      setIsKeyboardOpen(currentIsKeyboardOpen);
      console.log('📱 Keyboard detection - Window height:', window.innerHeight, 'Viewport height:', window.visualViewport?.height, 'Keyboard open:', currentIsKeyboardOpen);
    };

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const handleEdit = () => {
    console.log('Edit button clicked, current isEditing:', isEditing);
    // Toggle edit mode
    setIsEditing(!isEditing);
    // Reset unsaved changes when entering edit mode
    setHasUnsavedChanges(false);
  };

  // Track changes in edit data
  const handleEditDataChange = (newData) => {
    setEditData(newData);
    setHasUnsavedChanges(true);
  };

  const startVoiceEdit = async () => {
    if (isRecording) return; // Prevent multiple recordings
    
    console.log('🎤 Voice edit button pressed at:', new Date().toISOString());
    
    // Check microphone permissions first
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      console.log('🎤 Microphone permission status:', permissionStatus.state);
      
      if (permissionStatus.state === 'denied') {
        showToast('❌ Нет разрешения на микрофон', 'error');
        return;
      }
      
      if (permissionStatus.state === 'prompt') {
        showToast('🎤 Запрашиваю разрешение на микрофон...', 'info', 0);
        // Don't start recording yet, just request permissions
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          showToast('✅ Разрешение получено! Нажмите кнопку еще раз для записи', 'success', 3000);
        } catch (error) {
          console.error('Permission request failed:', error);
          showToast('❌ Ошибка доступа к микрофону', 'error');
        }
        return; // Exit here - don't start recording
      }
    } catch (error) {
      console.log('🎤 Permission API not supported, continuing...');
    }
    
    try {
      // Try to get microphone access - this will trigger permission prompt if needed
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Only start recording AFTER we successfully got access
      console.log('🎤 Microphone access granted, starting recording...');
      
      // Set recording start time only after successful access
      const startTime = Date.now();
      setRecordingStartTime(startTime);
      setRecordingDuration(0); // Reset timer
      console.log('🎤 Recording start time set to:', startTime);
      
      showToast('🎤 Начинаю запись...', 'info', 0); // 0 = permanent
      
      const recorder = new MediaRecorder(stream);
      const localAudioChunks = []; // Use local variable instead of state
      
      recorder.ondataavailable = (event) => {
        console.log('🎤 Data available, chunk size:', event.data.size);
        localAudioChunks.push(event.data);
      };
      
      setMediaRecorder(recorder);
      setIsRecording(true);
      setAudioChunks([]); // Reset audio chunks
      
      // Update refs for stable references
      mediaRecorderRef.current = recorder;
      isRecordingRef.current = true;
      
      recorder.start();
      console.log('🎤 Recording started at:', new Date().toISOString());
      showToast('🎤 Записываю... Нажмите кнопку еще раз для остановки', 'info', 0); // 0 = permanent
      
      // Store localAudioChunks reference for later use
      recorder._localAudioChunks = localAudioChunks;
      
    } catch (error) {
      console.error('Error starting voice recording:', error);
      showToast('❌ Ошибка доступа к микрофону', 'error');
      setIsRecording(false);
    }
  };

  const stopVoiceEdit = async () => {
    console.log('🛑 Voice edit button released at:', new Date().toISOString());
    
    // Prevent multiple calls using refs for stable references
    if (!mediaRecorderRef.current || !isRecordingRef.current) {
      console.log('🛑 No mediaRecorder or not recording, skipping');
      return;
    }
    
    // Check if recordingStartTime is valid
    if (!recordingStartTime || recordingStartTime === 0) {
      console.log('❌ Invalid recording start time:', recordingStartTime);
      showToast('❌ Ошибка: время начала записи не установлено', 'error');
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      mediaRecorderRef.current = null;
      setIsRecording(false);
      return;
    }
    
    const recordingDuration = Date.now() - recordingStartTime;
    console.log('🛑 Stopping voice recording... Duration:', recordingDuration, 'ms');
    console.log('🛑 Recording start time:', recordingStartTime);
    console.log('🛑 Current time:', Date.now());
    console.log('🛑 MediaRecorder state:', mediaRecorderRef.current.state);
    
    // Immediately reset state
    setIsRecording(false);
    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingDuration(0);
    
    // Update refs
    isRecordingRef.current = false;
    
    // Check if recording was too short (less than 500ms for better UX)
    if (recordingDuration < 500) {
      console.log('❌ Recording too short, duration:', recordingDuration, 'ms');
      showToast('❌ Запись слишком короткая, попробуйте еще раз', 'error', 0);
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      mediaRecorderRef.current = null;
      return;
    }
    
    console.log('🛑 Calling mediaRecorder.stop()');
    mediaRecorderRef.current.stop();
    
    // Stop all tracks to release microphone
    if (mediaRecorderRef.current.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    
    // Wait a bit for audio chunks to be collected
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Process audio immediately using local chunks
    try {
      console.log('🎤 Processing audio immediately...');
      const chunksToUse = mediaRecorderRef.current._localAudioChunks || audioChunks;
      console.log('🎤 Using chunks:', chunksToUse.length, 'chunks');
      const audioBlob = new Blob(chunksToUse, { type: 'audio/ogg' });
      console.log('🎤 Audio blob size:', audioBlob.size, 'bytes');
      
      if (audioBlob.size < 500) {
        console.log('❌ Audio too small, size:', audioBlob.size);
        showToast('❌ Запись слишком короткая, попробуйте еще раз', 'error');
        return;
      }
      
      // Start voice processing
      setIsVoiceProcessing(true);
      console.log('🔄 Starting transcription...');
      
      const transcription = await taskService.transcribeAudio(audioBlob);
      console.log('🔄 Transcription result:', transcription);
      
      if (transcription.text.trim()) {
        // Send voice edit to AI
        console.log('🤖 Sending voice edit to AI:', transcription.text);
        console.log('🤖 TaskId:', taskId);
        const result = await taskService.editTaskWithAI(taskId, task, transcription.text);
        console.log('🤖 AI edit result:', result);
        
        if (result) {
          console.log('🤖 Updating task with AI result:', result);
          // Update both the main task state and edit form data
          setTask(result);
          handleEditDataChange({
            taskName: result.text || '',
            tags: result.tags || [],
            contacts: result.contacts || [],
            places: result.places || [],
            checklist: result.checklist || []
          });
          
          showToast('✅ Задача изменена голосом!', 'success', 2000);
        } else {
          console.warn('🤖 No task data in AI response:', result);
          showToast('❌ Ошибка получения изменений от AI', 'error');
        }
      } else {
        showToast('❌ Не удалось распознать речь', 'error');
      }
    } catch (error) {
      console.error('Error processing voice edit:', error);
      showToast('❌ Ошибка обработки голоса', 'error');
    } finally {
      // Stop voice processing
      setIsVoiceProcessing(false);
      // Clean up refs
      mediaRecorderRef.current = null;
    }
  };

  const handleSaveEdit = async () => {
    try {
      console.log('💾 Saving task changes:', editData);
      console.log('💾 Current task status:', task.status);
      console.log('💾 Task ID:', taskId);
      
      // Prepare update data
      const updateData = {
        text: editData.taskName, // Backend expects 'text', not 'taskName'
        tags: editData.tags,
        contacts: editData.contacts,
        places: editData.places,
        checklist: editData.checklist.map((item, index) => ({
          text: item.text,
          isCompleted: item.isCompleted,
          order: index
        })) // Convert to backend format
      };
      
      console.log('📤 Sending update to API:', updateData);
      console.log('📤 Task ID:', taskId);
      console.log('📤 Current task before update:', task);
      console.log('📤 Checklist data:', editData.checklist);
      
      // Always use PUT for task updates - simpler and more reliable
      console.log('🔍 Current task status before save:', task.status);
      if (task.status === 'pending') {
        console.log('📝 Processing pending task - updating data and changing status');
        // For pending tasks, update data and change status to todo in one call
        const updateDataWithStatus = {
          text: updateData.text,
          dueAt: updateData.dueAt,
          priority: updateData.priority,
          tags: updateData.tags,
          contacts: updateData.contacts,
          places: updateData.places,
          checklist: updateData.checklist.map((item, index) => ({
            text: item.text,
            isCompleted: item.isCompleted,
            order: index
          })), // Convert to backend format
          status: 'todo' // Add status change to the update
        };
        console.log('📝 Calling updateTaskFull with status change:', updateDataWithStatus);
        const updatedTask = await taskService.updateTaskFull(taskId, updateDataWithStatus);
        console.log('📝 updateTaskFull response:', updatedTask);
        setTask(updatedTask);
        showToast('✅ Задача создана', 'success');
      } else {
        console.log('👁️ Processing non-pending task - updating data only');
        // For other tasks, just update the data
        const updateDataFormatted = {
          text: updateData.text,
          dueAt: updateData.dueAt,
          priority: updateData.priority,
          tags: updateData.tags,
          contacts: updateData.contacts,
          places: updateData.places,
          checklist: updateData.checklist.map((item, index) => ({
            text: item.text,
            isCompleted: item.isCompleted,
            order: index
          })) // Convert to backend format
        };
        console.log('👁️ Calling updateTaskFull with:', updateDataFormatted);
        const updatedTask = await taskService.updateTaskFull(taskId, updateDataFormatted);
        console.log('👁️ updateTaskFull response:', updatedTask);
        setTask(updatedTask);
        showToast('✅ Задача обновлена', 'success');
      }
      
      // Exit edit mode
      setIsEditing(false);
      setHasUnsavedChanges(false);
      
      // Debug: Check final status
      console.log('🔍 Final task status after save:', task.status);
      console.log('🔍 Final task data after save:', task);
      console.log('🔍 Is editing mode:', isEditing);
    } catch (err) {
      console.error('❌ Error saving task:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      console.error('❌ Task data that failed:', {
        taskId,
        taskStatus: task.status,
        editData,
        updateData: {
          text: editData.taskName,
          tags: editData.tags,
          contacts: editData.contacts,
          places: editData.places,
          checklist: editData.checklist
        }
      });
      showToast('❌ Ошибка сохранения задачи', 'error');
    }
  };

  const handleBackEdit = () => {
    setIsEditing(false);
    setOriginalText(task?.text || '');
    // Reset edit data to original
    setEditData({
      taskName: task?.text || '',
      tags: task?.tags || [],
      contacts: task?.contacts || [],
      places: task?.places || [],
      checklist: task?.checklist || [],
      notifications: task?.notifications || []
    });
    setHasUnsavedChanges(false);
  };

  // Tag functions
  const addTag = () => {
    if (newTag.trim()) {
      handleEditDataChange({
        ...editData,
        tags: [...editData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    handleEditDataChange({
      ...editData,
      tags: editData.tags.filter((_, i) => i !== index)
    });
  };

  // Notification functions
  const addNotification = () => {
    if (isValidNotification()) {
      const notification = {
        type: newNotification.type,
        dateTime: newNotification.type === 'once' ? newNotification.dateTime : null,
        rrule: newNotification.type === 'recurring' ? newNotification.rrule : null,
        interval: newNotification.type === 'interval' ? newNotification.interval : null,
        description: newNotification.description || null
      };
      
      handleEditDataChange({
        ...editData,
        notifications: [...editData.notifications, notification]
      });
      setNewNotification({ 
        type: 'once', 
        dateTime: '', 
        rrule: '', 
        interval: '', 
        description: '' 
      });
    }
  };

  const removeNotification = (index) => {
    handleEditDataChange({
      ...editData,
      notifications: editData.notifications.filter((_, i) => i !== index)
    });
  };

  const isValidNotification = () => {
    if (newNotification.type === 'once') {
      return newNotification.dateTime.trim() !== '';
    }
    if (newNotification.type === 'recurring') {
      return newNotification.description.trim() !== '';
    }
    if (newNotification.type === 'interval') {
      return newNotification.interval.trim() !== '';
    }
    return false;
  };

  // Contact functions
  const addContact = () => {
    if (newContact.name.trim()) {
      handleEditDataChange({
        ...editData,
        contacts: [...editData.contacts, { ...newContact }]
      });
      setNewContact({ name: '', phone: '', email: '' });
    }
  };

  const removeContact = (index) => {
    handleEditDataChange({
      ...editData,
      contacts: editData.contacts.filter((_, i) => i !== index)
    });
  };

  const editContact = (index, field, value) => {
    handleEditDataChange({
      ...editData,
      contacts: editData.contacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    });
  };

  // Place functions
  const addPlace = () => {
    if (newPlace.name.trim()) {
      handleEditDataChange({
        ...editData,
        places: [...editData.places, { ...newPlace }]
      });
      setNewPlace({ name: '', address: '' });
    }
  };

  const removePlace = (index) => {
    handleEditDataChange({
      ...editData,
      places: editData.places.filter((_, i) => i !== index)
    });
  };

  const editPlace = (index, field, value) => {
    handleEditDataChange({
      ...editData,
      places: editData.places.map((place, i) => 
        i === index ? { ...place, [field]: value } : place
      )
    });
  };

  // Checklist functions
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      handleEditDataChange({
        ...editData,
        checklist: [...editData.checklist, { text: newChecklistItem.trim(), isCompleted: false, order: editData.checklist.length }]
      });
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index) => {
    handleEditDataChange({
      ...editData,
      checklist: editData.checklist.filter((_, i) => i !== index)
    });
  };

  const toggleChecklistItem = (index) => {
    handleEditDataChange({
      ...editData,
      checklist: editData.checklist.map((item, i) => 
        i === index ? { ...item, isCompleted: !item.isCompleted } : item
      )
    });
  };

  // Toggle checklist item in view mode
  const toggleChecklistItemView = async (index) => {
    try {
      const updatedChecklist = task.checklist.map((item, i) => 
        i === index ? { ...item, isCompleted: !item.isCompleted } : item
      );
      
      await taskService.updateTaskFull(taskId, { 
        ...task,
        checklist: updatedChecklist 
      });
      
      // Update local state
      setTask(prev => ({
        ...prev,
        checklist: updatedChecklist
      }));
      
      showToast('✅ Чек-лист обновлен', 'success', 1500);
    } catch (err) {
      showToast('❌ Ошибка обновления', 'error');
    }
  };

  const handleComplete = async () => {
    try {
      if (task.status === 'pending') {
        // For pending tasks, update task data and change status to todo
        const updateData = {
          text: editData.taskName,
          tags: editData.tags,
          contacts: editData.contacts,
          places: editData.places,
          checklist: editData.checklist.map((item, index) => ({
            text: item.text,
            isCompleted: item.isCompleted,
            order: index
          })),
          status: 'todo' // Change status to todo
        };
        
        const updatedTask = await taskService.updateTaskFull(taskId, updateData);
        setTask(updatedTask);
        showToast('✅ Задача подтверждена', 'success');
      } else {
        // For other tasks, use completeTask method (PATCH /tasks/{id}/status/done)
        await taskService.completeTask(taskId);
        setTask(prev => ({ ...prev, status: 'done' }));
        showToast('✅ Задача выполнена', 'success');
      }
      navigate('/');
    } catch (err) {
      showToast('❌ Ошибка обновления', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка задачи...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">❌ {error || 'Задача не найдена'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            Книги
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 mobile-content">
      
      {/* Unsaved Changes Banner - Only show in edit mode */}
      {isEditing && hasUnsavedChanges && (
        <div className="fixed top-0 left-0 right-0 z-50 py-4 px-6 bg-amber-100 border-b-2 border-amber-500">
          <div className="flex items-center justify-center gap-3 text-amber-800">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
            <span className="text-lg font-semibold">Несохраненные изменения</span>
          </div>
        </div>
      )}

      {/* Voice Processing Status - Always show when processing */}
      {isVoiceProcessing && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Обрабатываю голос...</span>
          </div>
        </div>
      )}
      
      {/* Task Content */}
      <div className="p-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {isEditing ? (
            /* Edit Mode */
            <div className="space-y-6 pb-24">
              {/* Task Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название задачи:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editData.taskName}
                    onChange={(e) => handleEditDataChange({ ...editData, taskName: e.target.value })}
                    className="flex-1 text-xl font-bold text-gray-900 bg-gradient-to-r from-violet-50/50 to-purple-50/50 border-2 border-transparent hover:border-violet-200 focus:border-violet-500 focus:outline-none rounded-xl px-4 py-3 transition-all"
                    placeholder="Название задачи"
                  />
                </div>
              </div>

              {/* Edit Tabs */}
              <div className="space-y-4">
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('checklist')}
                    className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                      activeTab === 'checklist'
                        ? 'text-violet-600 border-b-2 border-violet-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Чек-лист"
                  >
                    📋
                  </button>
                  <button
                    onClick={() => setActiveTab('contacts')}
                    className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                      activeTab === 'contacts'
                        ? 'text-violet-600 border-b-2 border-violet-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Контакты"
                  >
                    👥
                  </button>
                  <button
                    onClick={() => setActiveTab('places')}
                    className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                      activeTab === 'places'
                        ? 'text-violet-600 border-b-2 border-violet-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Места"
                  >
                    📍
                  </button>
                  <button
                    onClick={() => setActiveTab('tags')}
                    className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                      activeTab === 'tags'
                        ? 'text-violet-600 border-b-2 border-violet-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Тэги"
                  >
                    🏷️
                  </button>
                  <button
                    onClick={() => setActiveTab('notifications')}
                    className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                      activeTab === 'notifications'
                        ? 'text-violet-600 border-b-2 border-violet-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Уведомления"
                  >
                    🔔
                  </button>
                </div>
                
                <div className="min-h-[300px]">
                  {activeTab === 'checklist' && (
                    <div className="space-y-4">
                      <div className="space-y-1">
                        {editData.checklist.map((item, index) => (
                          <div key={index} className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded">
                            <input
                              type="text"
                              value={item.text}
                              onChange={(e) => handleEditDataChange({
                                ...editData,
                                checklist: editData.checklist.map((checkItem, i) =>
                                  i === index ? { ...checkItem, text: e.target.value } : checkItem
                                )
                              })}
                              className="flex-1 p-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-violet-500"
                            />
                            <button
                              onClick={() => removeChecklistItem(index)}
                              className="text-red-500 hover:text-red-700 p-0.5"
                            >
                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          value={newChecklistItem}
                          onChange={(e) => setNewChecklistItem(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                          placeholder="Добавить пункт чек-листа..."
                          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-violet-500"
                        />
                        <button
                          onClick={addChecklistItem}
                          disabled={!newChecklistItem.trim()}
                          className="px-3 py-1.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded text-xs font-bold hover:from-violet-700 hover:to-purple-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'contacts' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        {editData.contacts.map((contact, index) => (
                          <div key={index} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                              <input
                                type="text"
                                value={contact.name}
                                onChange={(e) => editContact(index, 'name', e.target.value)}
                                placeholder="Имя"
                                className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                              />
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => editContact(index, 'phone', e.target.value)}
                                placeholder="Телефон"
                                className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                              />
                              <input
                                type="email"
                                value={contact.email}
                                onChange={(e) => editContact(index, 'email', e.target.value)}
                                placeholder="Email"
                                className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                              />
                            </div>
                            <button
                              onClick={() => removeContact(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Удалить контакт
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={newContact.name}
                          onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                          placeholder="Имя"
                          className="px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
                        />
                        <input
                          type="tel"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                          placeholder="Телефон"
                          className="px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
                        />
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newContact.email}
                            onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                            placeholder="Email"
                            className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
                          />
                          <button
                            onClick={addContact}
                            disabled={!newContact.name.trim()}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-cyan-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'places' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        {editData.places.map((place, index) => (
                          <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                              <input
                                type="text"
                                value={place.name}
                                onChange={(e) => editPlace(index, 'name', e.target.value)}
                                placeholder="Название места"
                                className="px-3 py-2 text-sm border border-green-200 rounded-lg focus:outline-none focus:border-green-500 bg-white"
                              />
                              <input
                                type="text"
                                value={place.address}
                                onChange={(e) => editPlace(index, 'address', e.target.value)}
                                placeholder="Адрес"
                                className="px-3 py-2 text-sm border border-green-200 rounded-lg focus:outline-none focus:border-green-500 bg-white"
                              />
                            </div>
                            <button
                              onClick={() => removePlace(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Удалить место
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={newPlace.name}
                          onChange={(e) => setNewPlace({...newPlace, name: e.target.value})}
                          placeholder="Название места"
                          className="px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:border-green-500 bg-green-50/50"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newPlace.address}
                            onChange={(e) => setNewPlace({...newPlace, address: e.target.value})}
                            placeholder="Адрес"
                            className="flex-1 px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:border-green-500 bg-green-50/50"
                          />
                          <button
                            onClick={addPlace}
                            disabled={!newPlace.name.trim()}
                            className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'tags' && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {editData.tags.map((tag, index) => (
                          <span key={index} className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-violet-100 to-purple-100 text-violet-800 rounded-xl text-sm font-semibold border border-violet-200 shadow-sm">
                            #{tag}
                            <button
                              onClick={() => removeTag(index)}
                              className="ml-2 text-violet-600 hover:text-violet-900 active:scale-90 transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addTag()}
                          placeholder="🏷️ Введите тег..."
                          className="flex-1 px-4 py-3 text-sm border-2 border-violet-300 rounded-xl focus:outline-none focus:border-violet-500 bg-violet-50/50 transition-all"
                        />
                        <button
                          onClick={addTag}
                          disabled={!newTag.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-700 hover:to-purple-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'notifications' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        {editData.notifications?.map((notification, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-violet-50 rounded-xl border border-violet-200">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-violet-600">🔔</span>
                                <span className="font-medium text-violet-900">
                                  {notification.type === 'once' && 'Разовое уведомление'}
                                  {notification.type === 'recurring' && 'Повторяющееся'}
                                  {notification.type === 'interval' && 'Интервальное'}
                                </span>
                              </div>
                              {notification.dateTime && (
                                <div className="text-sm text-violet-700">
                                  📅 {new Date(notification.dateTime).toLocaleString('ru-RU')}
                                </div>
                              )}
                              {notification.description && (
                                <div className="text-sm text-violet-600">
                                  {notification.description}
                                </div>
                              )}
                              {notification.interval && (
                                <div className="text-sm text-violet-600">
                                  ⏰ Каждые: {notification.interval}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeNotification(index)}
                              className="ml-2 text-violet-600 hover:text-violet-900 active:scale-90 transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <select
                            value={newNotification.type}
                            onChange={(e) => setNewNotification({...newNotification, type: e.target.value})}
                            className="flex-1 px-3 py-2 border-2 border-violet-300 rounded-lg focus:outline-none focus:border-violet-500 bg-violet-50/50"
                          >
                            <option value="once">Разовое</option>
                            <option value="recurring">Повторяющееся</option>
                            <option value="interval">Интервальное</option>
                          </select>
                        </div>
                        
                        {newNotification.type === 'once' && (
                          <input
                            type="datetime-local"
                            value={newNotification.dateTime}
                            onChange={(e) => setNewNotification({...newNotification, dateTime: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-violet-300 rounded-lg focus:outline-none focus:border-violet-500 bg-violet-50/50"
                            placeholder="Дата и время"
                          />
                        )}
                        
                        {newNotification.type === 'recurring' && (
                          <input
                            type="text"
                            value={newNotification.description}
                            onChange={(e) => setNewNotification({...newNotification, description: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-violet-300 rounded-lg focus:outline-none focus:border-violet-500 bg-violet-50/50"
                            placeholder="Описание (например: каждое утро)"
                          />
                        )}
                        
                        {newNotification.type === 'interval' && (
                          <input
                            type="text"
                            value={newNotification.interval}
                            onChange={(e) => setNewNotification({...newNotification, interval: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-violet-300 rounded-lg focus:outline-none focus:border-violet-500 bg-violet-50/50"
                            placeholder="Интервал (например: 2 часа, 30 минут)"
                          />
                        )}
                        
                        <button
                          onClick={addNotification}
                          disabled={!isValidNotification()}
                          className="w-full px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-700 hover:to-purple-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          + Добавить уведомление
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Fixed Bottom Buttons - Mobile Optimized */}
              <div className={`mobile-bottom-buttons safe-area-inset-bottom ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
                <div className="flex gap-1">
                  <button
                    onClick={handleBackEdit}
                    className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-gray-600 hover:to-gray-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                    </svg>
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </button>
                  <button
                    onClick={isRecording ? stopVoiceEdit : startVoiceEdit}
                    onContextMenu={(e) => e.preventDefault()} // Prevent context menu
                    className={`flex-1 text-white py-3 px-4 rounded-xl font-semibold hover:opacity-90 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm select-none touch-manipulation ${isRecording ? 'animate-pulse' : ''}`}
                    style={{
                      background: isRecording 
                        ? 'linear-gradient(to right, #ef4444, #dc2626)' 
                        : 'linear-gradient(to right, #8b5cf6, #7c3aed)',
                      userSelect: 'none',
                      webkitUserSelect: 'none',
                      webkitTouchCallout: 'none',
                      webkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation'
                    }}
                    disabled={loading}
                  >
                    {isRecording ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        <span className="text-sm font-mono">
                          {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                        </span>
                      </>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* View Mode */
            <div className="pb-24">
              {/* Task Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{task.text}</h2>
          
          {/* Status and Priority in one line */}
          <div className="mb-4 flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              task.status === 'done' ? 'bg-green-100 text-green-800' :
              task.status === 'inprocess' ? 'bg-blue-100 text-blue-800' :
              task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {task.status === 'done' ? '✅ Выполнено' :
               task.status === 'inprocess' ? '🔄 В процессе' :
               task.status === 'pending' ? '⏳ Ожидает' :
               '📝 К выполнению'}
            </span>
            
            {task.priority && (
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                task.priority === 'high' ? 'bg-red-100 text-red-800' :
                task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {task.priority === 'high' ? '🔴 Высокий' :
                 task.priority === 'medium' ? '🟡 Средний' :
                 '🔵 Низкий'}
              </span>
            )}
          </div>

          {/* Due Date */}
          {task.dueAt && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">📅 Срок выполнения:</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(task.dueAt).toLocaleString('ru-RU')}
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveTab('checklist')}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === 'checklist'
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Чек-лист"
              >
                📋
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === 'contacts'
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Контакты"
              >
                👥
              </button>
              <button
                onClick={() => setActiveTab('places')}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === 'places'
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Места"
              >
                📍
              </button>
              <button
                onClick={() => setActiveTab('tags')}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === 'tags'
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Тэги"
              >
                🏷️
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === 'notifications'
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Уведомления"
              >
                🔔
              </button>
            </div>
            
            <div className="min-h-[200px]">
              {activeTab === 'checklist' && (
                <div className="space-y-1">
                  {task.checklist && task.checklist.length > 0 ? (
                    task.checklist.map((item, index) => (
                      <div key={index} className="flex items-center space-x-1.5 p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                        <button
                          onClick={task.status === 'pending' ? undefined : () => toggleChecklistItemView(index)}
                          disabled={task.status === 'pending'}
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
                            task.status === 'pending' 
                              ? 'border-gray-200 bg-gray-100 cursor-not-allowed' 
                              : item.isCompleted 
                                ? 'bg-green-500 border-green-500 hover:scale-110' 
                                : 'border-gray-300 hover:border-green-400 hover:scale-110'
                          }`}
                        >
                          {item.isCompleted && (
                            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </button>
                        <span 
                          onClick={task.status === 'pending' ? undefined : () => toggleChecklistItemView(index)}
                          className={`flex-1 text-xs ${
                            task.status === 'pending' 
                              ? 'cursor-not-allowed text-gray-400' 
                              : `cursor-pointer select-none ${item.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`
                          }`}
                        >
                          {item.text}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">Чек-лист пуст</p>
                  )}
                </div>
              )}
              
              {activeTab === 'contacts' && (
                <div className="space-y-3">
                  {task.contacts && task.contacts.length > 0 ? (
                    task.contacts.map((contact, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-gray-900 mb-2">{contact.name}</p>
                        {contact.phone && (
                          <p className="text-sm text-gray-700 mb-1">📞 {contact.phone}</p>
                        )}
                        {contact.email && (
                          <p className="text-sm text-gray-700">📧 {contact.email}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">Контакты не указаны</p>
                  )}
                </div>
              )}
              
              {activeTab === 'places' && (
                <div className="space-y-3">
                  {task.places && task.places.length > 0 ? (
                    task.places.map((place, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-gray-900 mb-2">{place.name}</p>
                        {place.address && (
                          <p className="text-sm text-gray-700">🗺️ {place.address}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">Места не указаны</p>
                  )}
                </div>
              )}
              
              {activeTab === 'tags' && (
                <div className="space-y-3">
                  {task.tags && task.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {task.tags.map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Тэги не указаны</p>
                  )}
                </div>
              )}
              
              {activeTab === 'notifications' && (
                <div className="space-y-3">
                  {task.notifications && task.notifications.length > 0 ? (
                    task.notifications.map((notification, index) => (
                      <div key={index} className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-violet-600">🔔</span>
                          <span className="font-semibold text-violet-900">
                            {notification.type === 'once' && 'Разовое уведомление'}
                            {notification.type === 'recurring' && 'Повторяющееся уведомление'}
                            {notification.type === 'interval' && 'Интервальное уведомление'}
                          </span>
                        </div>
                        {notification.dateTime && (
                          <p className="text-sm text-violet-700 mb-1">
                            📅 {new Date(notification.dateTime).toLocaleString('ru-RU')}
                          </p>
                        )}
                        {notification.description && (
                          <p className="text-sm text-violet-600 mb-1">
                            {notification.description}
                          </p>
                        )}
                        {notification.interval && (
                          <p className="text-sm text-violet-600">
                            ⏰ Каждые: {notification.interval}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">Уведомления не настроены</p>
                  )}
                </div>
              )}
            </div>
          </div>

              {/* Actions - Fixed to bottom - Mobile Optimized */}
              <div className={`mobile-bottom-buttons safe-area-inset-bottom ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
                <div className="flex gap-1">
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-gray-600 hover:to-gray-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                    </svg>
                  </button>
                  {task.status !== 'done' && (
                    <button
                      onClick={handleComplete}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      {task.status === 'pending' ? 'Подтвердить' : 'Выполнить'}
                    </button>
                  )}
                  {task.status !== 'pending' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit();
                      }}
                      className="flex-1 bg-gradient-to-r from-violet-500 to-violet-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-violet-600 hover:to-violet-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mobile-button text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskView;
